<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { loadScript } from "@paypal/paypal-js";
import { useAuthStore } from "/src/stores/useAuthStore.js";
import { useCartStore } from "/src/stores/useCartStore.js";
import { useMeta, useQuasar } from "quasar";
import LoginForm from "/src/components/auth/LoginForm.vue";
import RegisterForm from "/src/components/auth/RegisterForm.vue";
import product_1 from "/src/assets/products/dentalgel_1.png";
import product_3 from "/src/assets/products/dentalgel_3.jpg";
import product_5 from "/src/assets/products/dentalgel_5.jpg";
import video_1 from "/src/assets/video/Oraklenz_landscape_long.mp4";
import fetch_request from "/src/requests/requests.js";

const url_base_img = import.meta.env.VUE_API_IMG;

const btn_label_auth = ref(false)

// ---- Facebook Meta Pixel ----
const FB_PIXEL_ID = import.meta.env.VUE_FB_PIXEL_ID;

const initFacebookPixel = () => {
  if (!FB_PIXEL_ID || typeof window === "undefined") return;
  if (window.fbq) return;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */

  window.fbq("init", FB_PIXEL_ID);
  window.fbq("track", "PageView");
};

const fbqTrack = (event, data = {}) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", event, data);
  }
};

useMeta(() => {
  return {
    title: "Oraklenz Health Gel — Reach Below the Gumline",
    meta: {
      description: {
        name: "description",
        content: "",
      },
    },
  };
});
const cartStore = useCartStore();
const authStore = useAuthStore();
const router = useRouter();
const $q = useQuasar();
const isCartOpen = ref(false);
const isAuthDialogOpen = ref(false);
const authTab = ref("login");

const handleProfileClick = () => {
  if (authStore.isAuthenticated) {
    router.push("/user-profile");
  } else {
    isAuthDialogOpen.value = true;
  }
};

const onAuthSuccess = () => {
  isAuthDialogOpen.value = false;
  router.push("/user-profile");
};
const isCheckoutStep = ref(false);
const isProcessing = ref(false);
const checkoutForm = ref({
  name: "",
  email: "",
  address: "",
});
const paypalButtonRef = ref(null);
const paypalOrderId = ref(null);
const paypalButtons = ref(null);
let paypalSdkPromise = null;

// ---- Product catalog (edit here to change pricing sitewide) ----
const products = ref([
  {
    id: "single-tube",
    variationId: 1,
    name: "Oraklenz Health Gel — Single Tube",
    price: 21.0,
    qty: 1,
    displayTitle: "Single Tube",
    sub: "A 30–45 day supply for most routines",
    comparePrice: "Leading gel: $26.00",
    perUnit: "$21.00 / tube",
    best: false,
    img: product_1,
  },
  {
    id: "bundle-3",
    variationId: 2,
    name: "Oraklenz Health Gel — 3-Tube Bundle",
    price: 61.0,
    qty: 1,
    displayTitle: "3-Tube Bundle",
    sub: "A full quarter's supply",
    comparePrice: "Leading gel: $76.00",
    perUnit: "$20.33 / tube",
    best: true,
    img: product_3,
  },
  {
    id: "bundle-5",
    variationId: 3,
    name: "Oraklenz Health Gel — 5-Tube Bundle",
    price: 96.0,
    qty: 1,
    displayTitle: "5-Tube Bundle",
    sub: "Best value per tube",
    comparePrice: "Leading gel: $119.00",
    perUnit: "$19.20 / tube",
    best: false,
    img: product_5,
  },
]);
const qtyState = ref({});

// The hero sells the single tube. Preferred by variation so the hero keeps
// working once the catalog is rebuilt from the API.
const singleTube = () =>
  products.value.find((p) => p.variationId === 1) || products.value[0];

const stepQty = (id, delta) => {
  const next = Math.max(1, (qtyState.value[id] || 1) + delta);
  qtyState.value[id] = next;
  const inCart = cartStore.items.some((item) => item.key === id);
  if (inCart && typeof cartStore.setQty === "function") {
    cartStore.setQty(id, next);
  }
};

const addToCart = (product) => {
  if (!product) {
    return;
  }
  if (typeof cartStore.add === "function") {
    cartStore.add({
      img: product.img,
      key: product.id,
      variation_id: product.variationId,
      name: product.name,
      price: product.price,
      qty: qtyState.value[product.id] || 1,
    });
  }
  fbqTrack("AddToCart", {
    content_name: product.name,
    content_ids: [product.id],
    content_type: "product",
    value: product.price * (qtyState.value[product.id] || 1),
    currency: "USD",
  });
  isCartOpen.value = true;
};

const removeFromCart = (key) => {
  if (typeof cartStore.remove === "function") {
    cartStore.remove(key);
  }
};

const checkout = () => {
  if (cartStore.items.length === 0) {
    $q.dialog({ title: "Cart", message: "Your cart is empty." });
    return;
  }
  fbqTrack("InitiateCheckout", {
    value: cartStore.subtotal,
    currency: "USD",
    num_items: cartStore.items.length,
    content_ids: cartStore.items.map((i) => i.key),
    content_type: "product",
  });
  isCheckoutStep.value = true;
};

// Paso 1: el backend crea la orden en PayPal y devuelve el paypal_order_id.
const createPayPalOrder = async () => {
  const amount = Number(cartStore.subtotal);
  if (cartStore.items.length === 0 || !Number.isFinite(amount) || amount <= 0) {
    const errorMsg =
      cartStore.items.length === 0
        ? "Your cart is empty."
        : "Could not create the PayPal order.";
    $q.dialog({
      title: cartStore.items.length === 0 ? "Cart" : "PayPal Error",
      message: errorMsg,
      color: "negative",
    });
    throw new Error(errorMsg);
  }

  const response = await fetch_request("/paypal", "POST", {
    amount: amount.toFixed(2),
  });

  if (!response || !response.paypal_order_id) {
    const errorMsg = response?.message || "Could not create the PayPal order.";
    $q.dialog({
      title: "PayPal Error",
      message: errorMsg,
      color: "negative",
    });
    throw new Error(errorMsg);
  }

  paypalOrderId.value = response.paypal_order_id;
  return response.paypal_order_id;
};

// Paso 4: el frontend le pide al backend capturar la orden aprobada.
const completeOrder = async () => {
  if (cartStore.items.length === 0) {
    $q.dialog({ title: "Cart", message: "Your cart is empty." });
    return;
  }
  if (!paypalOrderId.value) {
    $q.dialog({
      title: "PayPal Error",
      message: "PayPal payment was not approved. Please try again.",
      color: "negative",
    });
    return;
  }

  isProcessing.value = true;

  try {
    const payload = {
      items: cartStore.items,
      total_amount: cartStore.subtotal,
      checkout: checkoutForm.value,
      paypal_order_id: paypalOrderId.value,
    };

    const response = await fetch_request("/paypal/checkout", "POST", payload);

    if (response && response.success === true) {
      fbqTrack("Purchase", {
        value: cartStore.subtotal,
        currency: "USD",
        content_ids: cartStore.items.map((i) => i.key),
        content_type: "product",
        num_items: cartStore.items.length,
      });
      $q.dialog({
        title: "Success",
        message: "Order placed successfully!",
        color: "positive",
      });
      cartStore.items = [];
      paypalOrderId.value = null;
      isCheckoutStep.value = false;
      isCartOpen.value = false;
    } else {
      const errorMsg = response?.message || "Payment failed. Please try again.";
      $q.dialog({
        title: "Payment Error",
        message: errorMsg,
        color: "negative",
      });
    }
  } catch (e) {
    console.log(e);
    $q.dialog({
      title: "Payment Error",
      message: "Could not reach the payment server.",
      color: "negative",
    });
  } finally {
    isProcessing.value = false;
  }
};

const destroyPayPalButtons = () => {
  if (paypalButtons.value) {
    try {
      paypalButtons.value.close();
    } catch (e) {
      console.log(e);
    }
    paypalButtons.value = null;
  }
  if (paypalButtonRef.value) {
    paypalButtonRef.value.innerHTML = "";
  }
};

const renderPayPalButtons = async () => {
  if (
    !isCartOpen.value ||
    !isCheckoutStep.value ||
    cartStore.items.length === 0
  ) {
    destroyPayPalButtons();
    return;
  }

  await nextTick();

  let paypalNamespace;
  try {
    paypalNamespace = await loadPayPalSdk();
  } catch (e) {
    console.log("PayPal SDK failed to load", e);
    return;
  }

  await nextTick();

  paypalNamespace = paypalNamespace || window.paypal;
  if (
    !paypalNamespace ||
    typeof paypalNamespace.Buttons !== "function" ||
    !paypalButtonRef.value
  ) {
    return;
  }

  destroyPayPalButtons();
  paypalButtonRef.value.innerHTML = "";

  paypalButtons.value = paypalNamespace.Buttons({
    style: {
      layout: "vertical",
      color: "blue",
      shape: "rect",
      label: "paypal",
      height: 40,
    },
    // Paso 1 y 2: PayPal pide el id de la orden que ya creó tu backend.
    createOrder: () => createPayPalOrder(),
    // Paso 3: sólo guardamos el id aprobado. La captura la hace el backend.
    onApprove: async (data) => {
      paypalOrderId.value = data.orderID;
      await completeOrder();
    },
    onCancel: () => {
      isProcessing.value = false;
    },
    onError: (err) => {
      console.log(err);
      isProcessing.value = false;
      $q.dialog({
        title: "PayPal Error",
        message: "The payment could not be processed.",
        color: "negative",
      });
    },
  });

  if (typeof paypalButtons.value.render === "function") {
    await paypalButtons.value.render(paypalButtonRef.value);
  }
};

const loadPayPalSdk = () => {
  if (!paypalSdkPromise) {
    paypalSdkPromise = loadScript({
      clientId: import.meta.env.VUE_PAYPAL_CLIENT_ID,
      components: "buttons",
      currency: "USD",
      intent: "capture",
    });
  }
  return paypalSdkPromise;
};

watch([isCartOpen, isCheckoutStep], () => {
  renderPayPalButtons();
});

const handleDialogHide = () => {
  isCheckoutStep.value = false;
};

const goToId = (id) => {
  if (typeof document !== "undefined") {
    const element = document.getElementById(id);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }
};

const get_product_variation = async () => {
  try {
    const r = await fetch_request("/product", "GET");
    const variations = r?.data?.[0]?.variations;

    if (!Array.isArray(variations) || variations.length === 0) {
      throw new Error("Unexpected product payload shape");
    }

    const next = variations.map((e) => ({
      id: e.id,
      // The cart line carries the variation, and both payment paths refuse a
      // line without one. The API ids are variation ids.
      variationId: e.id,
      name: e.display_title,
      price: e.price,
      qty: e.stock,
      displayTitle: e.display_title,
      sub: e.sub_text,
      comparePrice: e.compare_price,
      perUnit: e.per_unit_text,
      best: e.display_title == "3-Tube Bundle" ? true : false,
      img: `${url_base_img}${e.image}`,
    }));

    products.value = next;

    next.forEach((obj) => {
      if (!(obj.id in qtyState.value)) {
        qtyState.value[obj.id] = 1;
      }
    });
  } catch (error) {
    console.error("No se pudo cargar el catálogo de productos:", error);
  }
};

onMounted(() => {
  get_product_variation();
  initFacebookPixel();
  btn_label_auth.value = authStore.isAuthenticated
});

onBeforeUnmount(() => {
  destroyPayPalButtons();
});
</script>
<template>
  <q-page>
    <div id="announce_id" class="announce">
      <b>Same active ingredient as leading perio gels.</b> ~20% less per tube.
      No subscription required.
    </div>

    <header>
      <div class="nav">
        <div
          class="text-h4 text-bold cursor-pointer"
          @click="goToId('announce_id')"
        >
          <span style="color: #002244">Ora</span
          ><span style="color: #2389c7">klenz</span>
        </div>
        <div class="navlinks cursor-pointer">
          <div @click="goToId('why')">Why Oraklenz</div>
          <div @click="goToId('video')">Watch It Work</div>
          <div @click="goToId('compare')">Compare</div>
          <div @click="goToId('reviews')">Reviews</div>
          <div @click="goToId('faq')">FAQ</div>
        </div>
        <div class="navright">
          <button
            class="cart-btn"
            aria-label="Open cart"
            @click="isCartOpen = true"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#002244"
              stroke-width="1.8"
            >
              <path
                d="M3 3h2l2.6 13.2a2 2 0 0 0 2 1.8h8.8a2 2 0 0 0 2-1.6L22 8H6"
              />
              <circle cx="9" cy="21" r="1.2" fill="#002244" />
              <circle cx="18" cy="21" r="1.2" fill="#002244" />
            </svg>
            <span id="cartCount" class="cart-count">{{
              cartStore.totalQty || 0
            }}</span>
          </button>
          <button class="btn btn-primary" @click="goToId('pricing-section')">
            Shop Now
          </button>
          <button class="btn btn-outline" @click="handleProfileClick">
            {{ btn_label_auth ? "My Account" : "LogIn" }}
          </button>
        </div>
      </div>
    </header>

    <!-- ================= HERO ================= -->
    <section class="hero">
      <div class="wrap hero-grid">
        <div>
          <span class="eyebrow">Hydrogen Peroxide Gum Therapy Gel</span>
          <h1>
            Reach below the gumline.<br />Where brushing <em>stops</em> working.
          </h1>
          <p class="lead">
            Oraklenz Health Gel is a dentist-formulated hydrogen peroxide gel
            that targets bacteria hiding below the gumline — the same active
            ingredient found in leading perio gels, made by a company that's
            supplied dental professionals for over 50 years.
          </p>
          <div class="price-chip">
            <span class="tag">$21 / tube</span>
            <span style="color: var(--ink-soft)"
              >vs. $26 leading competitor — same active ingredient</span
            >
          </div>
          <div class="hero-cta-row">
            <button class="btn btn-primary" @click="addToCart(singleTube())">
              Add to Cart — $21
            </button>
            <button class="btn btn-outline" @click="goToId('compare')">
              See the comparison
            </button>
          </div>
          <div class="trust-row">
            <div class="trust-item">
              ✓ Made by a 50+ year dental supply company
            </div>
            <div class="trust-item">✓ Works with or without trays</div>
            <div class="trust-item">✓ No subscription required</div>
          </div>
        </div>
        <div class="hero-visual">
          <div class="visual-card">
            <div class="gumline">
              <span class="tab">gumline</span>
            </div>
            <div
              class="bubble"
              style="
                width: 10px;
                height: 10px;
                left: 22%;
                bottom: 40%;
                animation-delay: 0s;
              "
            />
            <div
              class="bubble"
              style="
                width: 6px;
                height: 6px;
                left: 52%;
                bottom: 38%;
                animation-delay: 1.2s;
              "
            />
            <div
              class="bubble"
              style="
                width: 8px;
                height: 8px;
                left: 70%;
                bottom: 42%;
                animation-delay: 2.4s;
              "
            />
            <div
              class="bubble"
              style="
                width: 5px;
                height: 5px;
                left: 38%;
                bottom: 36%;
                animation-delay: 3.1s;
              "
            />
            <div class="visual-caption">
              <span class="num">ACTIVE INGREDIENT</span>
              <h3>Hydrogen peroxide, released where it matters</h3>
              <p>
                Formulated to help break down bacterial cell walls at the source
                of inflammation — not just on the surface enamel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ================= VIDEO ================= -->
    <section id="video" class="video-section">
      <div class="wrap">
        <div class="section-head">
          <span class="eyebrow">60 Seconds</span>
          <h2>Watch how Oraklenz works below the gumline</h2>
          <p>
            A quick walkthrough of the formula, how to apply it, and what to
            expect in the first two weeks.
          </p>
        </div>
        <div class="video-frame">
          <video
            controls
            autoplay
            muted
            playsinline
            style="
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              background: #000;
            "
            :src="video_1"
            loading="lazy"
          />
        </div>
        <!-- <div class="chapters">
          <div class="chapter">00:00<b>The problem</b></div>
          <div class="chapter">00:14<b>The formula</b></div>
          <div class="chapter">00:31<b>How to apply</b></div>
          <div class="chapter">00:48<b>Real results</b></div>
        </div> -->
      </div>
    </section>

    <!-- ================= STATS ================= -->
    <section
      id="why"
      style="
        background: var(--paper-raised);
        border-top: 1px solid var(--line);
        border-bottom: 1px solid var(--line);
      "
    >
      <div class="wrap row q-col-gutter-xl q-py-xl">
        <div class="col-12 col-sm-4">
          <div
            class="text-h3"
            style="
              font-family: var(--font-display);
              color: var(--teal-deep);
              line-height: 1;
            "
          >
            50+
          </div>
          <div
            class="text-body2 q-mt-sm"
            style="color: var(--ink-soft); max-width: 260px"
          >
            Years the manufacturer has supplied dental professionals directly.
          </div>
        </div>
        <div class="col-12 col-sm-4">
          <div
            class="text-h3"
            style="
              font-family: var(--font-display);
              color: var(--teal-deep);
              line-height: 1;
            "
          >
            ~20%
          </div>
          <div
            class="text-body2 q-mt-sm"
            style="color: var(--ink-soft); max-width: 260px"
          >
            Lower price per tube than the leading perio gel, same active
            ingredient.
          </div>
        </div>
        <div class="col-12 col-sm-4">
          <div
            class="text-h3"
            style="
              font-family: var(--font-display);
              color: var(--teal-deep);
              line-height: 1;
            "
          >
            2×
          </div>
          <div
            class="text-body2 q-mt-sm"
            style="color: var(--ink-soft); max-width: 260px"
          >
            Daily use fits into an existing morning and evening routine.
          </div>
          <!-- <div
            class="text-caption q-mt-xs"
            style="font-family: var(--font-mono); font-size: 10.5px; color: #4a6b8c;"
          >
            Verify all clinical/statistical claims against source data and your regulatory counsel before publishing.
          </div> -->
        </div>
      </div>
    </section>

    <!-- ================= HOW IT WORKS ================= -->
    <section>
      <div class="wrap">
        <div class="section-head">
          <span class="eyebrow">The Routine</span>
          <h2>Three steps, twice a day</h2>
          <p>
            Use with a prescribed tray for targeted delivery, or apply directly
            for daily maintenance.
          </p>
        </div>
        <div class="steps">
          <div class="step">
            <div class="stepnum">STEP 1</div>
            <h3>Apply</h3>
            <p>
              Dispense a thin line of gel into your tray to cover every tooth— a
              small amount goes a long way.
            </p>
          </div>
          <div class="step">
            <div class="stepnum">STEP 2</div>
            <h3>Wear</h3>
            <p>
              Leave in place for a minimum of 10 minutes while the formula works
              below the surface.
            </p>
          </div>
          <div class="step">
            <div class="stepnum">STEP 3</div>
            <h3>Rinse & go</h3>
            <p>
              Remove the tray. Rub your tongue along your teeth and spit out any
              excess that remains. No downtime, no taste to mask, no change to
              the rest of your routine.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ================= COMPARISON ================= -->
    <section id="compare">
      <div class="wrap">
        <div class="section-head">
          <span class="eyebrow">Side by Side</span>
          <h2>Oraklenz vs. the leading perio gel</h2>
          <p>
            Same category, same active ingredient — priced for people who don't
            want a subscription.
          </p>
        </div>
        <div class="compare-wrap">
          <table class="compare">
            <thead>
              <tr>
                <th>Feature</th>
                <th class="brand-col-ork brand-ok">Oraklenz Health Gel</th>
                <th>Leading Perio Gel</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="feature">Price, single tube</td>
                <td class="brand-col-ork ok">$21</td>
                <td>$26</td>
              </tr>
              <tr>
                <td class="feature">Price, 3-tube bundle</td>
                <td class="brand-col-ork ok">
                  $61
                  <span style="color: var(--ink-soft); font-weight: 400"
                    >($20.33/tube)</span
                  >
                </td>
                <td>$76 ($25.33/tube)</td>
              </tr>
              <tr>
                <td class="feature">Price, 5-tube bundle</td>
                <td class="brand-col-ork ok">
                  $96
                  <span style="color: var(--ink-soft); font-weight: 400"
                    >($19.20/tube)</span
                  >
                </td>
                <td>$119 ($23.80/tube)</td>
              </tr>
              <tr>
                <td class="feature">Active ingredient</td>
                <td class="brand-col-ork ok">Hydrogen peroxide</td>
                <td>Hydrogen peroxide</td>
              </tr>
              <tr>
                <td class="feature">Works with prescribed trays</td>
                <td class="brand-col-ork ok">Yes</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td class="feature">Subscription required to buy</td>
                <td class="brand-col-ork ok">No</td>
                <td>Recurring purchase agreement</td>
              </tr>
              <tr>
                <td class="feature">Manufacturer</td>
                <td class="brand-col-ork ok">SML — 50+ yrs in dental supply</td>
                <td>Perio Protect</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ================= TESTIMONIALS ================= -->
    <section id="reviews">
      <div class="wrap">
        <div class="section-head">
          <span class="eyebrow">From Real Customers</span>
          <h2>What people notice first</h2>
        </div>
        <div class="tmz">
          <div class="tcard">
            <div class="stars">★★★★★</div>
            <p>
              "We have been working with SML for several years now and every second of it has been wonderful. SML treats their employees well and they stand behind every product that leaves the door. Choose this lab if you are looking for a personable relationship and not just a transactional one."
            </p>
            <div class="who">— Ryan "RyGuy"</div>
            <!-- <span class="placeholder-flag">PLACEHOLDER — REPLACE</span> -->
          </div>
          <div class="tcard">
            <div class="stars">★★★★★</div>
            <p>
              "SML is a highly reliable Laboratory. Their customer service is excellent and the turnaround time and delivery dates are always on point. We have been using them for years and we are proud to recommend them to other Dentists around the country."
            </p>
            <div class="who">— Dr. Steven Lamberg</div>
            <!-- <span class="placeholder-flag">PLACEHOLDER — REPLACE</span> -->
          </div>
          <div class="tcard">
            <div class="stars">★★★★★</div>
            <p>
              "Their customer service is AMAZING. My OA was having issues and within minutes of contacting SML I got a call back from an expert who walked me through fixing it. He was super nice, super patient, and awesome. As a sufferer of severe obstructive sleep apnea having a working OA is extremely important and I feel very comfortable and happy with SML."
            </p>
            <div class="who">— Baruch Stone</div>
            <!-- <span class="placeholder-flag">PLACEHOLDER — REPLACE</span> -->
          </div>
        </div>
      </div>
    </section>

    <!-- ================= PRICING / CART ================= -->
    <section id="pricing-section">
      <div class="wrap">
        <div class="pricing">
          <div class="section-head">
            <span class="eyebrow">Choose Your Supply</span>
            <h2>Stock up and save — no subscription</h2>
            <p>One-time purchase. Cancel-anytime autoship coming soon.</p>
          </div>
          <div class="plan-grid">
            <div
              v-for="(product, key) in products"
              :id="'plan-' + key"
              :key="key"
              class="plan"
              :class="{ best: product.best }"
            >
              <img :src="product.img" loading="lazy" />
              <span v-if="product.best" class="best-badge">MOST POPULAR</span>
              <h3>{{ product.displayTitle }}</h3>
              <div class="sub">
                {{ product.sub }}
              </div>
              <div class="compare-price">
                {{ product.comparePrice }}
              </div>
              <div class="price">
                ${{ Math.floor(product.price) }}<small>.00</small>
              </div>
              <div class="perunit">
                {{ product.perUnit }}
              </div>
              <div class="qty-row">
                <button @click="stepQty(product.id, -1)">–</button>
                <span :id="'qty-' + product.id" class="qval">{{
                  qtyState[product.id] || 1
                }}</span>
                <button @click="stepQty(product.id, 1)">+</button>
              </div>
              <button
                class="btn add-btn"
                :class="product.best ? 'btn-primary' : 'btn-outline'"
                :style="
                  !product.best
                    ? 'color:#fff; border-color:rgba(255,255,255,.4);'
                    : ''
                "
                @click="addToCart(product)"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ================= FAQ ================= -->
    <section id="faq">
      <div class="wrap" style="max-width: 760px">
        <div class="section-head">
          <span class="eyebrow">Questions</span>
          <h2>Frequently asked</h2>
        </div>
        <q-list class="faq-list">
          <q-expansion-item
            group="faq"
            label="Do I need a prescribed tray to use the Oraklenz Gel."
            header-class="faq-q-header"
          >
            <q-card class="bg-transparent no-shadow">
              <q-card-section class="faq-a-body">
                No , Oraklenz gel has been designed to be used with any tray
                system. However an Oraklenz tray system is designed to create a
                seal that allows the Oraklenz Gel to work more effectively below
                the gumline.
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <q-expansion-item
            group="faq"
            label="How is this different from the leading perio gel?"
            header-class="faq-q-header"
          >
            <q-card class="bg-transparent no-shadow">
              <q-card-section class="faq-a-body">
                Both use hydrogen peroxide as the active ingredient. Our
                patients love the fresh minty taste. Oraklenz is made by SML, a
                dental supply company that's manufactured for professionals for
                over 50 years, and is priced roughly 20% lower with no
                subscription commitment.
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <q-expansion-item
            group="faq"
            label="How long does one tube last?"
            header-class="faq-q-header"
          >
            <q-card class="bg-transparent no-shadow">
              <q-card-section class="faq-a-body">
                Most customers get 30–45 days from a single tube with
                twice-daily use, depending on application method.
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <q-expansion-item
            group="faq"
            label="Is there a subscription?"
            header-class="faq-q-header"
          >
            <q-card class="bg-transparent no-shadow">
              <q-card-section class="faq-a-body">
                No. Every order on this page is a one-time purchase. An optional
                autoship option is planned for the future and will always be
                easy to cancel.
              </q-card-section>
            </q-card>
          </q-expansion-item>
        </q-list>
      </div>
    </section>

    <!-- ================= FOOTER ================= -->
    <footer>
      <div class="wrap">
        <div class="footer-grid">
          <div>
            <div class="logo" style="color: #fff">Ora<span>klenz</span></div>
            <p
              style="
                font-size: 13.5px;
                color: #8fa29e;
                max-width: 280px;
                margin-top: 12px;
              "
            >
              Dentist-formulated gum health gel from SML, a 50+ year dental
              supply manufacturer.
            </p>
          </div>
          <div>
            <h4>SHOP</h4>
            <div class="cursor-pointer" @click="goToId('pricing-section')">
              Single Tube
            </div>
            <div class="cursor-pointer" @click="goToId('pricing-section')">
              3-Tube Bundle
            </div>
            <div class="cursor-pointer" @click="goToId('pricing-section')">
              5-Tube Bundle
            </div>
          </div>
          <div>
            <h4>LEARN</h4>
            <div class="cursor-pointer" @click="goToId('compare')">Compare</div>
            <div class="cursor-pointer" @click="goToId('faq')">FAQ</div>
            <div class="cursor-pointer" @click="goToId('video')">
              Watch Video
            </div>
          </div>
          <div>
            <h4>SUPPORT</h4>
            <router-link to="/terms-of-service"> Terms of Service </router-link>
            <router-link to="/privacy-policy"> Privacy Policy </router-link>
          </div>
        </div>
        <div class="disclaimer">
          DRAFT COPY — REVIEW BEFORE LAUNCH. This page includes placeholder
          statistics, comparison pricing, and testimonials that must be
          verified, sourced, or replaced with real, verifiable customer reviews
          before publishing (FTC rules prohibit fabricated or unverifiable
          testimonials). Any statement suggesting this product treats, prevents,
          or cures gum disease, gingivitis, or periodontitis is a disease claim
          and should be reviewed against FDA OTC drug/medical device
          requirements and your reseller agreement with SML before it appears in
          public marketing. © Oraklenz. Not affiliated with Perio Protect® or
          PerioGel®; product names referenced for comparison purposes only.
        </div>
      </div>
    </footer>

    <!-- ================= CART DRAWER ================= -->
    <q-dialog
      v-model="isAuthDialogOpen"
      position="center"
      transition-show="fade"
      transition-hide="fade"
    >
      <q-card style="width: 400px; max-width: 90vw">
        <q-tabs
          v-model="authTab"
          dense
          class="text-center"
          active-color="primary"
          indicator-color="primary"
          align="justify"
        >
          <q-tab name="login" label="Ingresar" />
          <q-tab name="register" label="Registro" />
        </q-tabs>

        <q-tab-panels v-model="authTab" animated>
          <q-tab-panel name="login">
            <LoginForm @success="onAuthSuccess" />
          </q-tab-panel>
          <q-tab-panel name="register">
            <RegisterForm @success="onAuthSuccess" />
          </q-tab-panel>
        </q-tab-panels>
      </q-card>
    </q-dialog>

    <q-dialog
      v-model="isCartOpen"
      position="right"
      maximized
      @hide="handleDialogHide"
    >
      <q-card class="columns items-between full-height" style="width: 800px; max-width: 100vw">
        <div class="items-center q-pb-none q-pt-md q-px-md">
          <div class="text-h5 text-weight-bold flex items-center">
            <q-btn
              v-if="isCheckoutStep"
              icon="arrow_back"
              flat
              round
              dense
              class="q-mr-sm"
              @click="isCheckoutStep = false"
            />
            {{ isCheckoutStep ? "Checkout" : "Your Cart" }}
          </div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </div>

        <div class="q-pt-lg q-px-md scroll">
          <div v-if="!isCheckoutStep">
            <div
              v-if="cartStore.items.length === 0"
              class="text-center text-grey-7 q-pa-md"
            >
              Your cart is empty.
            </div>
            <template v-else>
              <q-list>
                <q-item
                  v-for="l in cartStore.items || []"
                  :key="l.key"
                  class="q-px-none q-mb-md items-start"
                >
                  <q-item-section avatar>
                    <q-avatar
                      square
                      size="70px"
                      class="rounded-borders text-subtitle2 text-weight-bold"
                    >
                      <img :src="l.img" loading="lazy" />
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-bold text-subtitle1">
                      {{ l.name }}
                    </q-item-label>
                    <q-item-label caption class="text-grey-8 q-mt-xs">
                      Qty {{ l.qty }} &middot; ${{
                        Number(l.price).toFixed(2)
                      }}
                      each
                    </q-item-label>
                    <q-item-label class="q-mt-xs">
                      <span
                        class="cursor-pointer text-caption text-weight-medium"
                        style="text-decoration: underline"
                        @click="removeFromCart(l.key)"
                        >Remove</span
                      >
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label
                      class="text-weight-bold text-black text-subtitle1"
                    >
                      ${{ (l.price * l.qty).toFixed(2) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </div>
          <div v-else>
            <div class="text-subtitle1 text-weight-bold q-mb-md">
              Shipping Details
            </div>
            <q-form class="q-gutter-md">
              <q-input
                v-model="checkoutForm.name"
                outlined
                label="Full Name"
                dense
              />
              <q-input
                v-model="checkoutForm.email"
                outlined
                label="Email Address"
                type="email"
                dense
              />
              <q-input
                v-model="checkoutForm.address"
                outlined
                label="Shipping Address"
                dense
              />
            </q-form>
          </div>
        </div>

        <div class="q-pa-md">
          <div
            class="col-12 row full-width justify-between q-mb-md text-weight-bold text-h6"
          >
            <span>Subtotal</span>
            <span>${{ cartStore.subtotal.toFixed(2) || "0.00" }}</span>
          </div>
          <div v-if="!isCheckoutStep" class="col-12">
          <q-btn
            
            color="dark"
            class="full-width q-py-sm text-weight-bold"
            unelevated
            label="Proceed to Checkout"
            @click="checkout()"
          /></div>
          <div
            v-else
            ref="paypalButtonRef"
            class="col-12"
            :class="{ 'is-disabled': isProcessing }"
          />

          <div
            class="text-caption text-grey-6 q-mt-md text-center full-width"
            style="font-size: 0.8rem"
          >
            Secure payment processed by PayPal. Shipping &amp; taxes at calculated checkout.
          </div>
        </div>
      </q-card>
    </q-dialog>
  </q-page>
</template>
<style>
:root {
  --ink: #002244;
  --ink-soft: #4a6b8c;
  --paper: #ffffff;
  --paper-raised: #ffffff;
  --teal-deep: #00437c;
  --teal-bright: #0077be;
  --teal-pale: #e6f0f9;
  --coral: #0077be;
  --coral-dark: #00437c;
  --line: #d1e3f0;
  --gold: #c9a24b;
  --radius: 14px;
  --maxw: 1180px;
  --font-display: "Fraunces", serif;
  --font-body: "Inter", sans-serif;
  --font-mono: "IBM Plex Mono", monospace;
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--font-body);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

img {
  max-width: 100%;
  display: block;
}

a {
  color: inherit;
}

.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 28px;
}

.eyebrow {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--teal-deep);
  font-weight: 600;
}

h1,
h2,
h3 {
  font-family: var(--font-display);
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 15px;
  padding: 14px 26px;
  border-radius: 999px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-primary {
  background: var(--coral);
  color: #fff;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 119, 190, 0.35);
}

.btn-outline {
  background: transparent;
  color: var(--teal-deep);
  border: 1.5px solid var(--teal-deep);
}

.btn-outline:hover {
  background: var(--teal-deep);
  color: #fff;
}

.btn-dark {
  background: var(--ink);
  color: #fff;
}

.btn-dark:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 34, 68, 0.35);
}

/* ---------- Announcement bar ---------- */
.announce {
  background: var(--teal-deep);
  color: #eaf6f3;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 12.5px;
  letter-spacing: 0.03em;
  padding: 9px 12px;
}

.announce b {
  color: #fff;
}

/* ---------- Header ---------- */
header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--line);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  max-width: var(--maxw);
  margin: 0 auto;
}

.logo {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.logo span {
  color: var(--teal-bright);
}

.navlinks {
  display: flex;
  gap: 28px;
  font-size: 14.5px;
  font-weight: 500;
}

.navlinks a {
  text-decoration: none;
  color: var(--ink-soft);
}

.navlinks a:hover {
  color: var(--ink);
}

.navright {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cart-btn {
  position: relative;
  background: none;
  border: 1.5px solid var(--line);
  border-radius: 999px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.cart-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: var(--coral);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  width: 19px;
  height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
}

@media (max-width: 860px) {
  .navlinks {
    display: none;
  }
}

/* ---------- Hero ---------- */
.hero {
  padding: 64px 0 40px;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 56px;
  align-items: center;
}

@media (max-width: 920px) {
  .hero-grid {
    grid-template-columns: 1fr;
  }
}

.hero h1 {
  font-size: clamp(34px, 4.6vw, 54px);
  line-height: 1.03;
  margin: 14px 0 18px;
}

.hero h1 em {
  font-style: normal;
  color: var(--teal-bright);
}

.hero p.lead {
  font-size: 17.5px;
  color: var(--ink-soft);
  max-width: 480px;
  margin: 0 0 24px;
}

.price-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 8px 16px 8px 8px;
  font-size: 13.5px;
  margin-bottom: 26px;
}

.price-chip .tag {
  background: var(--teal-pale);
  color: var(--teal-deep);
  font-family: var(--font-mono);
  font-weight: 600;
  font-size: 12px;
  padding: 4px 9px;
  border-radius: 999px;
}

.hero-cta-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.trust-row {
  display: flex;
  gap: 26px;
  flex-wrap: wrap;
}

.trust-item {
  font-size: 13px;
  color: var(--ink-soft);
  display: flex;
  align-items: center;
  gap: 7px;
}

.trust-item svg {
  flex: none;
}

/* gumline visual signature element */
.hero-visual {
  position: relative;
}

.visual-card {
  background: linear-gradient(180deg, #00437c 0%, #002244 100%);
  border-radius: 24px;
  padding: 40px 34px 34px;
  position: relative;
  overflow: hidden;
  min-height: 440px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.gumline {
  position: absolute;
  left: 0;
  right: 0;
  top: 52%;
  border-top: 2px dashed rgba(255, 255, 255, 0.45);
}

.gumline .tab {
  position: absolute;
  right: 24px;
  top: -11px;
  background: #ffffff;
  color: var(--teal-deep);
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: 6px;
  font-weight: 600;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 119, 190, 0.55);
  animation: rise 4.5s ease-in infinite;
}

@keyframes rise {
  0% {
    transform: translateY(0);
    opacity: 0;
  }

  15% {
    opacity: 1;
  }

  100% {
    transform: translateY(-220px);
    opacity: 0;
  }
}

.visual-caption {
  color: #ffffff;
  position: relative;
  z-index: 2;
}

.visual-caption .num {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--teal-bright);
  letter-spacing: 0.08em;
}

.visual-caption h3 {
  color: #fff;
  font-size: 22px;
  margin: 8px 0 6px;
}

.visual-caption p {
  color: #d1e3f0;
  font-size: 14px;
  margin: 0;
  max-width: 340px;
}

/* ---------- Section shell ---------- */
section {
  padding: 76px 0;
}

.section-head {
  max-width: 640px;
  margin: 0 0 44px;
}

.section-head h2 {
  font-size: clamp(26px, 3.2vw, 38px);
  margin-top: 10px;
}

.section-head p {
  color: var(--ink-soft);
  font-size: 16px;
  margin-top: 12px;
}

.divider {
  border: none;
  border-top: 1px solid var(--line);
  margin: 0;
}

/* ---------- Video section ---------- */
.video-section {
  background: var(--teal-deep);
  color: #fff;
}

.video-section .eyebrow {
  color: var(--teal-bright);
}

.video-section .section-head h2 {
  color: #fff;
}

.video-section .section-head p {
  color: #d1e3f0;
}

.video-frame {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  background: #002244;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.video-frame img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.55;
}

.play-btn {
  position: relative;
  z-index: 2;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: var(--coral);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  box-shadow: 0 0 0 14px rgba(0, 119, 190, 0.18);
}

.chapters {
  display: flex;
  gap: 0;
  margin-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  flex-wrap: wrap;
}

.chapter {
  flex: 1;
  min-width: 150px;
  padding: 16px 4px 0;
  font-family: var(--font-mono);
  font-size: 12.5px;
  color: #d1e3f0;
}

.chapter b {
  display: block;
  color: #fff;
  font-family: var(--font-body);
  font-size: 14.5px;
  font-weight: 600;
  margin-top: 4px;
}

/* ---------- Stat band ---------- */
.stat-band {
  background: var(--paper-raised);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  padding: 52px 0;
}

@media (max-width: 760px) {
  .stat-grid {
    grid-template-columns: 1fr;
  }
}

.stat {
  text-align: left;
}

.stat .num {
  font-family: var(--font-display);
  font-size: 48px;
  color: var(--teal-deep);
  line-height: 1;
}

.stat .label {
  font-size: 14px;
  color: var(--ink-soft);
  margin-top: 8px;
  max-width: 260px;
}

.stat .cite {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: #4a6b8c;
  margin-top: 6px;
}

/* ---------- How it works ---------- */
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

@media (max-width: 820px) {
  .steps {
    grid-template-columns: 1fr;
  }
}

.step {
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px;
}

.step .stepnum {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--teal-bright);
  letter-spacing: 0.08em;
}

.step h3 {
  font-size: 20px;
  margin: 12px 0 8px;
}

.step p {
  color: var(--ink-soft);
  font-size: 14.5px;
  margin: 0;
}

/* ---------- Comparison ---------- */
.compare-wrap {
  overflow-x: auto;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

table.compare {
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;
}

table.compare th,
table.compare td {
  padding: 16px 20px;
  text-align: left;
  font-size: 14.5px;
  border-bottom: 1px solid var(--line);
}

table.compare thead th {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-soft);
  border-bottom: 2px solid var(--line);
}

table.compare th.brand-ok {
  color: var(--teal-deep);
}

table.compare td.ok {
  color: var(--teal-deep);
  font-weight: 600;
}

table.compare tr:last-child td {
  border-bottom: none;
}

table.compare td.feature {
  color: var(--ink-soft);
}

.brand-col-ork {
  background: var(--teal-pale);
}

.compare-note {
  font-size: 12.5px;
  color: var(--ink-soft);
  margin-top: 14px;
}

/* ---------- Testimonials ---------- */
.tmz {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

@media (max-width: 820px) {
  .tmz {
    grid-template-columns: 1fr;
  }
}

.tcard {
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 26px;
}

.stars {
  color: var(--gold);
  font-size: 14px;
  letter-spacing: 2px;
  margin-bottom: 12px;
}

.tcard p {
  font-size: 14.5px;
  color: var(--ink);
  margin: 0 0 14px;
}

.tcard .who {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-soft);
}

.placeholder-flag {
  display: inline-block;
  margin-top: 10px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--coral-dark);
  background: #e6f0f9;
  padding: 3px 8px;
  border-radius: 6px;
}

/* ---------- Pricing / Cart ---------- */
.pricing {
  background: var(--teal-deep);
  color: #fff;
  border-radius: 28px;
  padding: 52px 40px;
}

.pricing .section-head h2 {
  color: #fff;
}

.pricing .section-head p {
  color: #d1e3f0;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 10px;
}

@media (max-width: 820px) {
  .plan-grid {
    grid-template-columns: 1fr;
  }
}

.plan {
  background: #002244;
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 26px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.plan.best {
  border-color: var(--teal-bright);
  box-shadow: 0 0 0 1px var(--teal-bright);
}

.plan .best-badge {
  position: absolute;
  top: -12px;
  left: 20px;
  background: var(--teal-bright);
  color: #ffffff;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
}

.plan h3 {
  color: #fff;
  font-size: 17px;
  margin: 6px 0 4px;
}

.plan .sub {
  color: #d1e3f0;
  font-size: 12.5px;
  margin-bottom: 16px;
}

.plan .price {
  font-family: var(--font-display);
  font-size: 34px;
  color: #fff;
}

.plan .price small {
  font-size: 14px;
  color: #d1e3f0;
  font-weight: 400;
  font-family: var(--font-body);
}

.plan .perunit {
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--teal-bright);
  margin-bottom: 18px;
}

.plan .compare-price {
  font-size: 12.5px;
  color: #4a6b8c;
  text-decoration: line-through;
  margin-bottom: 2px;
}

.qty-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0 16px;
}

.qty-row button {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}

.qty-row .qval {
  font-family: var(--font-mono);
  font-size: 14px;
  min-width: 18px;
  text-align: center;
}

.add-btn {
  margin-top: auto;
  width: 100%;
}

/* ---------- FAQ ---------- */
.faq-list {
  border-top: 1px solid var(--line);
}

.faq-q-header {
  font-weight: 600;
  font-size: 15.5px;
  padding: 20px 0;
  border-bottom: 1px solid var(--line);
}

.faq-q-header:hover {
  color: var(--teal-deep);
}

.faq-a-body {
  color: var(--ink-soft);
  font-size: 16.5px;
  margin: 0;
  padding: 0 0 20px 0;
  max-width: 640px;
  border-bottom: 1px solid var(--line);
}

/* ---------- Guarantee ---------- */
.guarantee {
  display: flex;
  align-items: center;
  gap: 28px;
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 32px;
}

.guarantee .badge-circle {
  flex: none;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: var(--teal-pale);
  color: var(--teal-deep);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 11px;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.3;
}

@media (max-width: 600px) {
  .guarantee {
    flex-direction: column;
    text-align: center;
  }
}

/* ---------- Footer ---------- */
footer {
  background: var(--ink);
  color: #d1e3f0;
  padding: 52px 0 28px;
  margin-top: 60px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 32px;
}

@media (max-width: 760px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
  }
}

footer h4 {
  color: #fff;
  font-size: 13px;
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: 0 0 14px;
}

footer a {
  display: block;
  color: #d1e3f0;
  text-decoration: none;
  font-size: 13.5px;
  margin-bottom: 9px;
}

footer a:hover {
  color: #fff;
}

.disclaimer {
  font-size: 11.5px;
  color: #4a6b8c;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 36px;
  padding-top: 22px;
  line-height: 1.6;
}

/* ---------- Cart drawer ---------- */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 34, 68, 0.45);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
  z-index: 90;
}

.overlay.show {
  opacity: 1;
  pointer-events: auto;
}

.drawer {
  position: fixed;
  top: 0;
  right: -420px;
  width: 400px;
  max-width: 92vw;
  height: 100%;
  background: var(--paper-raised);
  z-index: 95;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
}

.drawer.show {
  right: 0;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 24px;
  border-bottom: 1px solid var(--line);
}

.drawer-head h3 {
  font-size: 19px;
}

.drawer-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--ink-soft);
}

.drawer-items {
  flex: 1;
  overflow-y: auto;
  padding: 18px 24px;
}

.drawer-empty {
  color: var(--ink-soft);
  font-size: 14px;
  text-align: center;
  padding: 60px 0;
}

.cart-line {
  display: flex;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}

.cart-line .thumb {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: var(--teal-pale);
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--teal-deep);
}

.cart-line .info {
  flex: 1;
}

.cart-line .info .name {
  font-size: 14px;
  font-weight: 600;
}

.cart-line .info .meta {
  font-size: 12px;
  color: var(--ink-soft);
  margin-top: 2px;
}

.cart-line .info .rm {
  font-size: 12px;
  color: var(--coral-dark);
  cursor: pointer;
  margin-top: 6px;
  display: inline-block;
}

.cart-line .price {
  font-family: var(--font-mono);
  font-size: 13.5px;
  white-space: nowrap;
}

.drawer-foot {
  padding: 20px 24px 26px;
  border-top: 1px solid var(--line);
}

.drawer-subtotal {
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 14px;
}

.drawer-foot .btn {
  width: 100%;
}

.drawer-note {
  font-size: 11.5px;
  color: var(--ink-soft);
  text-align: center;
  margin-top: 10px;
}

/* mobile sticky buy bar */
.sticky-buy {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--paper-raised);
  border-top: 1px solid var(--line);
  padding: 12px 18px;
  display: none;
  align-items: center;
  justify-content: space-between;
  z-index: 60;
}

.sticky-buy .price {
  font-family: var(--font-mono);
  font-weight: 600;
}

@media (max-width: 640px) {
  .sticky-buy {
    display: flex;
  }

  body {
    padding-bottom: 70px;
  }
}

/* ---------- PayPal button ---------- */
.paypal-button-container {
  width: 100%;
  min-height: 0;
}

.paypal-button-container.is-disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
